const SITE_ORIGIN = 'https://homeandown.com';

export interface PropertySeoEntry {
  keywords: string;
  region: string;
  place: string;
  title: string;
  description: string;
}

export interface PropertyMetaConfig {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
}

function buildKeywords(entry: PropertySeoEntry): string {
  return [entry.keywords, entry.region, entry.place, 'Hyderabad', 'Home and Own']
    .filter(Boolean)
    .join(', ');
}

function toMeta(path: string, entry: PropertySeoEntry): PropertyMetaConfig {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return {
    title: entry.title,
    description: entry.description,
    keywords: buildKeywords(entry),
    canonical: `${SITE_ORIGIN}${normalizedPath}`,
  };
}

/** SEO metadata from digital marketing — keyed by property path (e.g. /property/viswanadh-serenity). */
const PROPERTY_SEO_ENTRIES: Record<string, PropertySeoEntry> = {
  '/property/viswanadh-serenity': {
    keywords: 'Viswanadh Serenity',
    region: 'Gundlapochampalli',
    place: 'Villa',
    title: 'Viswanadh Serenity | Home and Own Hyderabad | Property',
    description:
      'Explore Viswanadh Serenity Property with Home and Own. Find premium villas, pricing, amenities, floor plans, and investment opportunities in Hyderabad.',
  },
  '/property/silver-sands-gr8-planet-villa-project': {
    keywords: 'Silver Sands GR8 Planet Villa Project',
    region: 'Eklaskhanpeta',
    place: 'Villa',
    title: 'Silver Sands GR8 Planet Villas | Home and Own | Property',
    description:
      'Discover Silver Sands GR8 Planet Villa Project with Home and Own at Hyderabad. Explore luxury villas, modern amenities, pricing, and investment options.',
  },
  '/property/lotus-green-luxury-villa': {
    keywords: 'Lotus Green Luxury Villa',
    region: 'Kandi mandal',
    place: 'Villa',
    title: 'Lotus Green Luxury Villa | Home and Own Hyderabad | Property',
    description:
      'Explore Lotus Green Luxury Villa with Home and Own located at Hyderabad. Discover premium villas, elegant designs, amenities, pricing, and investment potential.',
  },
  '/property/nvr-ramakrishna-neopolis': {
    keywords: 'NVR Ramakrishna Neopolis',
    region: 'Kompally',
    place: 'Gated Community Apartment',
    title: 'NVR Ramakrishna Neopolis | Home and Own | Property',
    description:
      'Find NVR Ramakrishna Neopolis with Home and Own located at Hyderabad. Explore premium apartments, floor plans, amenities, pricing, and investment opportunities.',
  },
  '/property/aaditri-exotica': {
    keywords: 'Aaditri exotica',
    region: 'Brodipet(Guntur)',
    place: 'Gated Community Apartment',
    title: 'Aaditri Exotica | Home and Own Hyderabad | Property',
    description:
      'Discover Aaditri Exotica in Bodipet with Home and Own Hyderabad. Explore premium homes, modern amenities, pricing, floor plans, and investment opportunities.',
  },
  '/property/aaditris-empire': {
    keywords: 'Aaditris Empire',
    region: 'Kollur',
    place: 'Gated Community Apartment',
    title: 'Aaditris Empire | Home and Own Hyderabad | Property',
    description:
      'Explore Aaditris Empire with Home and Own located at Hyderabad. Find premium residences, amenities, pricing, floor plans, and excellent investment opportunities',
  },
  '/property/aaditri-elite': {
    keywords: 'Aaditri Elite',
    region: 'Vikas Nagar, Guntur',
    place: 'Gated Community Apartment',
    title: 'Aaditri Elite | Home and Own Hyderabad |  Property',
    description:
      'Discover Aaditri Elite at Vikas Nagar with Home and Own. Explore premium apartments, amenities, pricing, floor plans, and smart investment opportunities.',
  },
  '/property/shivantha-gardenia': {
    keywords: 'Shivantha gardenia',
    region: 'isnapur',
    place: 'Gated Community Apartment',
    title: 'Shivantha Gardenia | Home and Own Hyderabad | Property',
    description:
      'Explore Shivantha Gardenia with Home and Own located at Hyderabad. Find premium apartments, modern amenities, pricing, floor plans, and investment opportunities',
  },
  '/property/avantika-constructions': {
    keywords: 'Avantika Constructions',
    region: 'A.I.E. R.C.puram, Medak',
    place: 'Gated Community Apartment',
    title: 'Avantika Constructions | Home and Own Hyderabad | Property',
    description:
      'Discover Avantika Constructions with Home and Own Hyderabad. Explore quality residential projects, pricing, amenities, floor plans, and investment options.',
  },
  '/property/alpine-gmr-springfield': {
    keywords: 'Alpine GMB Springfield',
    region: 'Hakimpet',
    place: 'Gated Community Apartment',
    title: 'Alpine GMR Springfield | Home and Own Hyderabad | Property',
    description:
      'Explore Alpine GMR Springfield with Home and Own located at Hyderabad. Discover premium homes, amenities, pricing, floor plans, and investment opportunities.',
  },
  '/property/diamond-city-plots': {
    keywords: 'Diamond City Plots',
    region: 'Annaram, Mahabub Nagar',
    place: 'Land',
    title: 'Diamond City Plots | Home and Own Hyderabad | Property',
    description:
      'Discover Diamond City Plots with Home and Own Hyderabad. Explore premium residential plots, location advantages, pricing, and investment opportunities.',
  },
  '/property/san-golden-city-1': {
    keywords: 'San Golden City 1',
    region: 'Annaram, Mahabub Nagar',
    place: 'Land',
    title: 'San Golden City 1 | Home and Own Hyderabad | Property',
    description:
      'Explore San Golden City 1 with Home and Own located at Hyderabad. Find premium residential plots, pricing, location benefits, and investment opportunities.',
  },
  '/property/suprabatha': {
    keywords: 'Suprabatha',
    region: 'Boddulurupadu, Guntur',
    place: 'Villa',
    title: 'Suprabatha | Home and Own Hyderabad | Property Listing',
    description:
      'Discover Suprabatha at Boddulurupadu with Home and Own. Explore premium residential villas, amenities, pricing, benefits, and investment opportunities.',
  },
  '/property/akshaya-vasusri-pride': {
    keywords: 'Akshaya Vasusri Pride',
    region: 'Airforce Academy, Hyderabad',
    place: 'Gated Community Apartment',
    title: 'Akshaya Vasusri Pride | Home and Own Hyderabad | Property',
    description:
      'Explore Akshaya Vasusri Pride with Home and Own. Discover premium apartments, amenities, floor plans, pricing, and investment opportunities in Hyderabad.',
  },
  '/property/aditri-everest': {
    keywords: 'Aditri Everest',
    region: 'apartments in chandanagar',
    place: 'Gated Community Apartment',
    title: 'Aditri Everest | Home and Own Hyderabad | Property',
    description:
      'Discover Aditri Everest with Home and Own Hyderabad. Explore premium residences, modern amenities, floor plans, pricing, and excellent investment opportunities.',
  },
  '/property/ss-indraprastha': {
    keywords: 'SS Indrprastha',
    region: 'apartments in chandanagar',
    place: 'Gated Community Apartment',
    title: 'SS Indraprastha | Home and Own Hyderabad | Property',
    description:
      'Explore SS Indraprastha with Home and Own Hyderabad. Find premium apartments, amenities, floor plans, pricing, and smart investment opportunities in Hyderabad.',
  },
  '/property/nyla': {
    keywords: 'Nyla',
    region: 'apartments in chandanagar',
    place: 'Gated Community Apartment',
    title: 'Nyla Property | Home and Own Hyderabad | Property Listing',
    description:
      'Discover Nyla with Home and Own Hyderabad. Explore premium apartments, modern amenities, floor plans, pricing, and investment opportunities in Hyderabad.',
  },
  '/property/vertex-viraat': {
    keywords: 'Vertex Viraat',
    region: 'apartments in chandanagar',
    place: 'Gated Community Apartment',
    title: 'Vertex Viraat | Home and Own Hyderabad |  Property',
    description:
      'Explore Vertex Viraat with Home and Own Hyderabad. Discover premium apartments, amenities, pricing, floor plans, and investment opportunities in Hyderabad.',
  },
  '/property/sai-keerthi-signature': {
    keywords: 'Sai Keerthi Signature',
    region: 'Kondapur, K.V.Rangareddy',
    place: 'Gated Community Apartment',
    title: 'Sai Keerthi Signature | Home and Own Hyderabad | Property',
    description:
      'Discover Sai Keerthi Signature with Home and Own Hyderabad. Explore premium apartments, floor plans, amenities, pricing, and investment opportunities.',
  },
  '/property/nebula-aavaas': {
    keywords: 'Nebula Aavaas',
    region: 'Bachupally, K.V.Rangareddy',
    place: 'Gated Community Apartment',
    title: 'Nebula Aavaas | Home and Own Hyderabad |  Property',
    description:
      'Explore Nebula Aavaas with Home and Own Hyderabad. Find premium apartments, modern amenities, floor plans, pricing, and investment opportunities in Hyderabad.',
  },
  '/property/sree-vista': {
    keywords: 'Sree Vista',
    region: 'Chetlapotharam, Medak',
    place: 'Gated Community Apartment',
    title: 'Sree Vista | Home and Own Hyderabad | Property Listing',
    description:
      'Discover Sree Vista with Home and Own located in Hyderabad. Explore premium apartments, quality amenities, floor plans, pricing, and investment opportunities.',
  },
  '/property/jaydev-villas': {
    keywords: 'Jaydev Villas',
    region: 'apartments in chandanagar',
    place: 'Gated Community Apartment',
    title: 'Jaydev Villas | Home and Own Hyderabad | Property Listing',
    description:
      'Explore Jaydev Villas with Home and Own Hyderabad. Discover luxury villas, premium amenities, pricing, floor plans, and investment opportunities in Hyderabad.',
  },
  '/property/royal-orchids-properties': {
    keywords: 'Royal Orchids Properties',
    region: 'apartments in chandanagar',
    place: 'Gated Community Apartment',
    title: 'Royal Orchids Properties |  Home and Own Hyderabad',
    description:
      'Discover Royal Orchids Properties with Home and Own located at Hyderabad. Explore premium homes, amenities, pricing, floor plans, and investment opportunities.',
  },
  '/property/janapriya-upscale-bahiti': {
    keywords: 'Janapriya Upscale Bahiti',
    region: 'Mathrusri Nagar, K.V.Rangareddy',
    place: 'Gated Community Apartment',
    title: 'Janapriya Upscale Bahiti | Home and Own Hyderabad | Property',
    description:
      'Explore Janapriya Upscale Bahiti with Home and Own Hyderabad. Discover premium apartments, amenities, floor plans, pricing, and investment opportunities.',
  },
  '/property/ksr-greens': {
    keywords: 'KSR Green',
    region: 'Madeenaguda, Chandanagar',
    place: 'Gated Community Apartment',
    title: 'KSR Greens | Home and Own Hyderabad | Property Listing',
    description:
      'Discover KSR Greens with Home and Own Hyderabad. Explore premium residential plots, location advantages, pricing, and investment opportunities in Hyderabad.',
  },
  '/property/agriculturalfarm-land': {
    keywords: 'Agricultural/Farm Land',
    region: 'Atmakur, Telangana',
    place: 'Land',
    title: 'Agricultural Farm Land | Home and Own Hyderabad | Property',
    description:
      'Explore Agricultural Farm Land with Home and Own Hyderabad. Find quality farmland, investment opportunities, pricing, and prime locations near Hyderabad.',
  },
  '/property/rajapushpa-serene-dale': {
    keywords: 'Rajapushpa Serene Dale',
    region: 'A.I.E. R.C.puram, Telangana',
    place: 'Villa',
    title: 'Rajapushpa Serene Dale | Home and Own Hyderabad | Property',
    description:
      'Discover Rajapushpa Serene Dale with Home and Own Hyderabad. Explore premium residential villas, amenities, floor plans, pricing, and investment opportunities.',
  },
  '/property/pragati-green-heights': {
    keywords: 'Pragati Green heights',
    region: 'Aziz Nagar, Telangana',
    place: 'Farm House',
    title: 'Pragati Green Heights | Home and Own Hyderabad | Property',
    description:
      'Explore Pragati Green Heights with Home and Own Hyderabad. Discover premium apartments, amenities, floor plans, pricing, and investment opportunities.',
  },
  '/property/ragha-heights': {
    keywords: 'Ragha heights',
    region: 'apartments in chandanagar',
    place: 'Gated Community Apartment',
    title: 'Ragha Heights | Home and Own Hyderabad | Property Listing',
    description:
      'Discover Ragha Heights with Home and Own located at Hyderabad. Explore premium apartments, modern amenities, floor plans, pricing, and investment opportunities.',
  },
  '/property/kolla-luxor-exclusive': {
    keywords: 'Kolla Luxor Exclusive',
    region: 'apartments in chandanagar',
    place: 'Independent House',
    title: 'Kolla Luxor Exclusive | Home and Own Hyderabad | Property',
    description:
      'Explore Kolla Luxor Exclusive with Home and Own Hyderabad. Discover luxury residences, premium amenities, pricing, floor plans, and investment opportunities.',
  },
  '/property/jb-galaxy-fountain-park': {
    keywords: 'JB Galaxy Fountain Park',
    region: 'Hyderabad, Serilingampalle mandal',
    place: 'Gated Community Apartment',
    title: 'JB Galaxy Fountain Park | Home and Own Hyderabad | Property',
    description:
      'Discover JB Galaxy Fountain Park with Home and Own Hyderabad. Explore premium apartments, amenities, floor plans, pricing, and investment opportunities.',
  },
  '/property/aditya-fortune-heights': {
    keywords: 'Aditya Fortune Heights',
    region: 'Hyderabad, Serilingampalle mandal',
    place: 'Gated Community Apartment',
    title: 'Aditya Fortune Heights | Home and Own Hyderabad | Property',
    description:
      'Explore Aditya Fortune Heights with Home and Own located at Hyderabad. Find premium apartments, amenities, floor plans, pricing, and investment opportunities.',
  },
  '/property/dhathri-vanam': {
    keywords: 'Dhathri Vanam',
    region: 'Kothavalasa',
    place: 'Land',
    title: 'Dhathri Vanam | Home and Own Hyderabad | Property Listing',
    description:
      'Discover Dhathri Vanam with Home and Own located at Hyderabad. Explore premium residential plots, pricing, location benefits, and investment opportunities.',
  },
};

/** Alternate slug spellings that should resolve to the same SEO entry. */
const PROPERTY_SEO_ALIASES: Record<string, string> = {
  '/property/viswanath-serenity': '/property/viswanadh-serenity',
};

function normalizePropertyPath(pathname: string): string {
  const key = (pathname || '').replace(/\/+$/, '').toLowerCase();
  return PROPERTY_SEO_ALIASES[key] || key;
}

export const PROPERTY_SEO: Record<string, PropertyMetaConfig> = Object.fromEntries(
  Object.entries(PROPERTY_SEO_ENTRIES).map(([path, entry]) => [path, toMeta(path, entry)])
);

/** Look up marketing SEO for a property URL path. */
export function getPropertySeoMeta(pathname: string): PropertyMetaConfig | undefined {
  const key = normalizePropertyPath(pathname);
  return PROPERTY_SEO[key];
}

export function isPropertySeoPath(pathname: string): boolean {
  return !!getPropertySeoMeta(pathname);
}
